import React, { useState, useEffect, useRef } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
  Flame, Beef, Wheat, Droplets, Briefcase, ArrowRight, Mail, CheckCircle2,
} from 'lucide-react';
import { toast } from 'sonner';
import { calculateMacros, sendMacroPlanEmail } from '../services/api';
import { trackLead } from '../utils/metaPixel';

/**
 * Phase 2 — Personalized Macronutrient Calculator
 *
 * Renders only when `open === true`. Inherits gender/age/height/weight from
 * the parent (Phase 1 Adonis/Venus form). Computes daily kcal + macros via
 * Mifflin-St Jeor → TDEE → goal-adjusted. On CTA click, persists the macro
 * snapshot to sessionStorage (key="oc_macro_snapshot") + navigates to
 * #programs so that CheckoutModal (in ProgramsSection) can attach it to
 * the payment payload — this surfaces the user's exact personalized macros
 * inside the post-purchase Welcome Email.
 */

export const MACRO_SNAPSHOT_KEY = 'oc_macro_snapshot';

const ACTIVITY_OPTIONS = [
  { v: 'sedentary_office', label: '🪑 Ülőmunka (napi 6+ óra ülés) — ×1.2' },
  { v: 'standing_light',   label: '🚶 Állómunka / Könnyű fizikai munka — ×1.4' },
  { v: 'heavy_shift',      label: '🏭 12 órás folyamatos műszak / Nehéz fizikai munka — ×1.7' },
];

const GOAL_OPTIONS = [
  { v: 'fat_loss',    l: 'Fogyás / Szálkásítás',          s: '-500 kcal · 2.2 g/kg' },
  { v: 'maintenance', l: 'Testkompozíció / Jobb közérzet', s: 'TDEE · 1.6 g/kg' },
  { v: 'muscle_gain', l: 'Tiszta izomtömeg-növelés',       s: '+300 kcal · 2.0 g/kg' },
];

const GOAL_PROTEIN_RATIONALE = {
  fat_loss:
    'Kalóriadeficitben a szervezet hajlamos az izomszövetet is lebontani energiáért. A kutatások szerint a megemelt, 2.2 g/ttkg-os fehérjebevitel elengedhetetlen a tiszta izomtömeg megóvásához és a jóllakottsághoz.',
  muscle_gain:
    'Izomtömeg-növeléskor a kalóriatöbblet önmagában is fehérjespóroló hatású. Ebben a fázisban a 2.0 g/ttkg-os arány a tudományos arany középút az optimális izomépítéshez.',
  maintenance:
    'Ha a célod az általános tónusosabb fizikum és jobb közérzet, a napi 1.6 g/ttkg-os fehérjebevitel tökéletesen elegendő az izomzat regenerációjához és az egészséges működéshez.',
};

const MacroPhase = ({ open, baseProfile }) => {
  const [form, setForm] = useState({
    activity_level: 'sedentary_office',
    goal: 'fat_loss',
  });
  const [result, setResult] = useState(null);
  const [isCalculating, setIsCalculating] = useState(false);

  // ---- "Küldjétek el e-mailben" (manual) form state ----
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [emailInput, setEmailInput] = useState('');
  const [nameInput, setNameInput] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  // Auto-trigger: prevents duplicate sends for the same (email, goal) tuple
  // within the current tab session. Backend also dedups on 24h window.
  const autoSentKeyRef = useRef('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    const weightNum = parseFloat(baseProfile.weight_kg);
    const ageNum = parseInt(baseProfile.age_years, 10);
    if (!weightNum || weightNum < 30) {
      toast.error('Add meg a testsúlyodat (kg) a Phase 1 űrlapon.');
      return;
    }
    if (!ageNum || ageNum < 14) {
      toast.error('Add meg az életkorodat a Phase 1 űrlapon.');
      return;
    }
    setIsCalculating(true);
    try {
      const payload = {
        gender:         baseProfile.gender,
        age:            ageNum,
        height_cm:      parseFloat(baseProfile.height_cm),
        weight_kg:      weightNum,
        activity_level: form.activity_level,
        goal:           form.goal,
      };
      const res = await calculateMacros(payload);
      setResult(res);
      // Persist snapshot for CheckoutModal pick-up
      try {
        sessionStorage.setItem(MACRO_SNAPSHOT_KEY, JSON.stringify({
          target_kcal:    res.target_kcal,
          protein_g:      res.protein_g,
          carbs_g:        res.carbs_g,
          fat_g:          res.fat_g,
          activity_label: res.activity_label,
          goal:           res.goal,
          saved_at:       new Date().toISOString(),
        }));
      } catch { /* sessionStorage might be unavailable in some browsers */ }
      setTimeout(() => {
        const el = document.getElementById('aesthetics-macro-results');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 80);
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Makró számítási hiba.';
      toast.error(typeof detail === 'string' ? detail : 'Ellenőrizd az adatokat.');
    } finally {
      setIsCalculating(false);
    }
  };

  // --------- Manual "E-mailben kérem" submit -----------------------------
  const handleManualSend = async (e) => {
    e.preventDefault();
    if (!result) return;
    const emailTrim = emailInput.trim();
    if (!emailTrim) {
      toast.error('Add meg az e-mail címedet.');
      return;
    }
    setIsSending(true);
    try {
      const weightNum = parseFloat(baseProfile.weight_kg);
      await sendMacroPlanEmail({
        email:     emailTrim,
        name:      nameInput.trim() || null,
        source:    'manual',
        macros:    result,
        weight_kg: weightNum,
        age:       parseInt(baseProfile.age_years, 10) || null,
        height_cm: parseFloat(baseProfile.height_cm) || null,
      });
      setEmailSent(true);
      setShowEmailForm(false);
      // Cache the email so a subsequent goal-change auto-sends without prompt.
      try { sessionStorage.setItem('oc_calc_email', emailTrim); } catch { /* noop */ }
      toast.success('Elküldtük a makró-tervedet! ✉️');
      // Meta Pixel: high-value lead capture (calculator email opt-in).
      trackLead({ content_name: 'macro_calculator_email' });
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Az e-mail küldése nem sikerült.';
      toast.error(typeof detail === 'string' ? detail : 'Próbáld újra kicsit később.');
    } finally {
      setIsSending(false);
    }
  };

  // --------- Auto-send after a returning saved-email user computes a plan --
  useEffect(() => {
    if (!result) return;
    let savedEmail = '';
    try {
      savedEmail = sessionStorage.getItem('oc_calc_email') || '';
    } catch { /* noop */ }
    if (!savedEmail) return;
    // Once per (email, goal) tuple in the current session.
    const key = `${savedEmail}::${result.goal}`;
    if (autoSentKeyRef.current === key) return;
    autoSentKeyRef.current = key;
    // Fire and forget — backend also dedups on 24h window, so double-fires
    // from stale cached emails or repeated re-computes are safe.
    (async () => {
      try {
        const resp = await sendMacroPlanEmail({
          email:     savedEmail,
          source:    'auto',
          macros:    result,
          weight_kg: parseFloat(baseProfile.weight_kg),
          age:       parseInt(baseProfile.age_years, 10) || null,
          height_cm: parseFloat(baseProfile.height_cm) || null,
        });
        if (resp?.email_status === 'sent') {
          setEmailSent(true);
        }
      } catch {
        /* silent — auto-send is best-effort */
      }
    })();
  }, [result, baseProfile.weight_kg]);

  const handleCheckoutCtaClick = () => {
    // Tag the navigation so ProgramsSection / CheckoutModal can surface a
    // confirmation banner. The snapshot is already in sessionStorage.
    // We dispatch the custom event (sessionStorage is the source of truth);
    // we deliberately do NOT modify URL search params here because the
    // anchor's href already navigates to '#programs?from=macro-calc'.
    try {
      window.dispatchEvent(new CustomEvent('oc:open-checkout-from-macro', {
        detail: { source: 'macro-calc' },
      }));
    } catch { /* noop */ }
    // Let the anchor navigation proceed normally
    // (don't preventDefault — we still want the smooth scroll to #programs)
  };

  // All hooks are declared above. Only NOW may we bail out — this keeps
  // React's rules-of-hooks happy across every render.
  if (!open) return null;

  return (
    <section
      id="aesthetics-macro-phase"
      data-testid="aesthetics-macro-phase"
      className="mb-8 rounded-2xl bg-gradient-to-br from-gray-950 to-gray-900 text-white border border-yellow-600/30 p-6 md:p-10 shadow-2xl animate-in fade-in slide-in-from-bottom-4 duration-500"
    >
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <div className="inline-flex items-center gap-2 mb-3 px-3 py-1 bg-yellow-500/10 border border-yellow-500/30 rounded-full">
            <Flame className="text-yellow-400" size={14} />
            <span className="text-yellow-400 font-semibold text-xs tracking-widest uppercase">
              Phase 2 · Makrotápanyagok
            </span>
          </div>
          <h3
            className="text-2xl md:text-3xl font-bold leading-tight"
            style={{ fontFamily: 'Montserrat, sans-serif' }}
          >
            Személyre szabott napi makrók
          </h3>
          <p className="text-sm text-gray-300 mt-2 max-w-xl">
            Mifflin-St Jeor BMR-rel és <span className="text-yellow-400 font-semibold">a te műszakodhoz illesztve</span>.
          </p>
        </div>
      </div>

      {/* Inherited values summary */}
      <div
        data-testid="aesthetics-macro-inherited"
        className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6 text-xs"
      >
        {[
          { label: 'Nem',      value: baseProfile.gender === 'male' ? 'Férfi' : 'Nő' },
          { label: 'Életkor',  value: baseProfile.age_years ? `${baseProfile.age_years} év` : '—' },
          { label: 'Magasság', value: baseProfile.height_cm ? `${baseProfile.height_cm} cm` : '—' },
          { label: 'Testsúly', value: baseProfile.weight_kg ? `${baseProfile.weight_kg} kg` : '—' },
        ].map((it) => (
          <div key={it.label} className="bg-white/5 border border-white/10 rounded-lg p-3">
            <div className="text-gray-400 text-[10px] tracking-widest uppercase font-bold mb-1">{it.label}</div>
            <div className="text-white font-semibold text-sm">{it.value}</div>
          </div>
        ))}
      </div>

      <form onSubmit={handleSubmit} data-testid="aesthetics-macro-form" className="space-y-5">
        {/* Activity level dropdown */}
        <div>
          <Label className="text-gray-200 font-semibold mb-2 flex items-center gap-2">
            <Briefcase size={16} className="text-yellow-400" />
            Munka jellege / Aktivitási szint
          </Label>
          <Select
            value={form.activity_level}
            onValueChange={(v) => setForm((p) => ({ ...p, activity_level: v }))}
          >
            <SelectTrigger
              data-testid="aesthetics-macro-activity"
              className="h-12 bg-white/5 border-white/15 text-white hover:bg-white/10"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {ACTIVITY_OPTIONS.map((o) => (
                <SelectItem key={o.v} value={o.v}>{o.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {form.activity_level === 'heavy_shift' && (
            <p className="text-[11px] text-yellow-400 mt-2 flex items-start gap-1.5">
              <span>⚠️</span>
              <span>
                Folyamatos műszak — a magasabb (×1.7) szorzó kompenzál a hosszú,
                álló-mozgó terhelésért.
              </span>
            </p>
          )}
        </div>

        {/* Goal toggle */}
        <div>
          <Label className="text-gray-200 font-semibold mb-2 block">Cél</Label>
          <div
            data-testid="aesthetics-macro-goal-toggle"
            className="grid grid-cols-1 sm:grid-cols-3 gap-2 p-1 bg-white/5 border border-white/10 rounded-xl"
          >
            {GOAL_OPTIONS.map((opt) => (
              <button
                key={opt.v}
                type="button"
                data-testid={`aesthetics-macro-goal-${opt.v}`}
                onClick={() => setForm((p) => ({ ...p, goal: opt.v }))}
                className={`flex sm:block items-center justify-between gap-3 px-4 sm:px-3 py-3 rounded-lg transition-all text-left sm:text-center ${
                  form.goal === opt.v
                    ? 'bg-yellow-500 text-gray-900 shadow-lg shadow-yellow-500/20'
                    : 'text-gray-300 hover:bg-white/5 hover:text-white'
                }`}
              >
                <div className="text-[13px] sm:text-sm font-semibold leading-tight break-words min-w-0">{opt.l}</div>
                <div className={`text-[10px] sm:mt-0.5 whitespace-nowrap shrink-0 ${form.goal === opt.v ? 'text-gray-900/70' : 'text-gray-400'}`}>{opt.s}</div>
              </button>
            ))}
          </div>
        </div>

        <div>
          <Button
            type="submit"
            data-testid="aesthetics-macro-submit-btn"
            disabled={isCalculating}
            className="w-full bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-bold text-base py-6 rounded-xl shadow-xl shadow-yellow-500/20 transition-all"
          >
            {isCalculating ? 'Számolás…' : 'Makrók kiszámítása'}
            <ArrowRight className="ml-2" size={18} />
          </Button>
        </div>
      </form>

      {/* Phase 2 Results */}
      {result && (
        <div
          id="aesthetics-macro-results"
          data-testid="aesthetics-macro-results"
          className="mt-8 pt-8 border-t border-white/10 animate-in fade-in slide-in-from-bottom-2 duration-500"
        >
          {/* Calories hero */}
          <div className="bg-gradient-to-r from-yellow-500/15 to-yellow-600/10 border border-yellow-500/30 rounded-2xl p-6 md:p-8 mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <div className="text-xs font-bold tracking-widest uppercase text-yellow-400 mb-2 flex items-center gap-2">
                <Flame size={14} /> Napi cél kalória
              </div>
              <div
                data-testid="aesthetics-macro-target-kcal"
                className="text-4xl sm:text-5xl md:text-6xl font-bold leading-none text-white break-words"
                style={{ fontFamily: 'Montserrat, sans-serif' }}
              >
                {result.target_kcal.toLocaleString('hu-HU')}
                <span className="text-xl text-yellow-400 ml-2">kcal</span>
              </div>
              <div className="text-xs text-gray-400 mt-2">
                BMR {result.bmr_kcal.toLocaleString('hu-HU')} · TDEE {result.tdee_kcal.toLocaleString('hu-HU')} · {result.goal_adjustment >= 0 ? '+' : ''}{result.goal_adjustment} kcal
              </div>
            </div>
            <div className="text-left md:text-right text-xs text-gray-300">
              <div className="font-semibold text-white mb-1">{result.activity_label}</div>
              <div>×{result.activity_factor} aktivitás-szorzó</div>
            </div>
          </div>

          {/* Macro grid */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
            <div
              data-testid="aesthetics-macro-protein"
              className="bg-white/5 border border-white/10 rounded-xl p-5 hover:border-yellow-500/40 transition-all"
            >
              <div className="flex items-center gap-2 text-yellow-400 text-[10px] font-bold tracking-widest uppercase mb-2">
                <Beef size={14} /> Fehérje
              </div>
              <div className="text-3xl font-bold text-white" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                {result.protein_g}<span className="text-lg text-gray-400 ml-1">g</span>
              </div>
              <div className="text-[11px] text-gray-400 mt-1">{result.protein_kcal} kcal · {Math.round((result.protein_kcal / result.target_kcal) * 100)}% · {result.protein_per_kg} g/kg</div>
            </div>

            <div
              data-testid="aesthetics-macro-carbs"
              className="bg-white/5 border border-white/10 rounded-xl p-5 hover:border-yellow-500/40 transition-all"
            >
              <div className="flex items-center gap-2 text-yellow-400 text-[10px] font-bold tracking-widest uppercase mb-2">
                <Wheat size={14} /> Szénhidrát
              </div>
              <div className="text-3xl font-bold text-white" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                {result.carbs_g}<span className="text-lg text-gray-400 ml-1">g</span>
              </div>
              <div className="text-[11px] text-gray-400 mt-1">{result.carbs_kcal} kcal · {Math.round((result.carbs_kcal / result.target_kcal) * 100)}% · maradék</div>
            </div>

            <div
              data-testid="aesthetics-macro-fats"
              className="bg-white/5 border border-white/10 rounded-xl p-5 hover:border-yellow-500/40 transition-all"
            >
              <div className="flex items-center gap-2 text-yellow-400 text-[10px] font-bold tracking-widest uppercase mb-2">
                <Droplets size={14} /> Zsír
              </div>
              <div className="text-3xl font-bold text-white" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                {result.fat_g}<span className="text-lg text-gray-400 ml-1">g</span>
              </div>
              <div className="text-[11px] text-gray-400 mt-1">{result.fat_kcal} kcal · {Math.round((result.fat_kcal / result.target_kcal) * 100)}%</div>
            </div>
          </div>

          {/* Goal-based science-rationale helper block */}
          {GOAL_PROTEIN_RATIONALE[result.goal] && (
            <div
              data-testid="aesthetics-macro-rationale"
              className="mb-6 rounded-xl border border-yellow-500/25 bg-yellow-500/[0.06] p-4 md:p-5 flex items-start gap-3"
            >
              <div className="shrink-0 w-9 h-9 rounded-lg bg-yellow-500/15 border border-yellow-500/30 grid place-items-center">
                <Beef size={16} className="text-yellow-400" />
              </div>
              <div className="min-w-0">
                <div className="text-[10px] font-bold tracking-widest uppercase text-yellow-400 mb-1">
                  Tudomány a fehérje cél mögött · {result.protein_per_kg} g/ttkg
                </div>
                <p className="text-[13px] text-gray-200 leading-relaxed">
                  {GOAL_PROTEIN_RATIONALE[result.goal]}
                </p>
              </div>
            </div>
          )}

          {/* --- Email the plan (manual + auto) ---------------------------- */}
          <div
            data-testid="aesthetics-macro-email-block"
            className="mb-6 rounded-xl border border-white/10 bg-white/[0.03] p-4 md:p-5"
          >
            {emailSent ? (
              <div className="flex items-start gap-3" data-testid="aesthetics-macro-email-success">
                <CheckCircle2 className="text-emerald-400 flex-shrink-0 mt-0.5" size={20} />
                <div>
                  <div className="text-sm font-semibold text-white">Kiküldtük a makró-tervedet ✉️</div>
                  <p className="text-xs text-gray-400 mt-1">
                    Nézd meg a bejövő fiókodat (ha nem látod, ellenőrizd a promóciók / spam mappát is).
                  </p>
                </div>
              </div>
            ) : !showEmailForm ? (
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div className="flex items-start gap-3 min-w-0">
                  <div className="shrink-0 w-9 h-9 rounded-lg bg-yellow-500/15 border border-yellow-500/30 grid place-items-center">
                    <Mail size={16} className="text-yellow-400" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-white">Küldjük el e-mailben?</div>
                    <p className="text-xs text-gray-400 mt-1 leading-relaxed">
                      Így bármikor előhívhatod a mobilodon — és nem kell újraszámolnod, ha visszatérsz.
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  data-testid="aesthetics-macro-email-open-btn"
                  onClick={() => setShowEmailForm(true)}
                  className="whitespace-nowrap inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/15 border border-white/15 text-white font-semibold text-sm px-4 py-2 rounded-lg transition-colors"
                >
                  <Mail size={14} /> E-mailben kérem
                </button>
              </div>
            ) : (
              <form onSubmit={handleManualSend} data-testid="aesthetics-macro-email-form" className="space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <Label htmlFor="macro-email" className="text-xs text-gray-300">E-mail cím</Label>
                    <Input
                      id="macro-email"
                      data-testid="aesthetics-macro-email-input"
                      type="email"
                      required
                      value={emailInput}
                      onChange={(e) => setEmailInput(e.target.value)}
                      placeholder="pl. kovacs.janos@pelda.hu"
                      className="mt-1 bg-white/5 border-white/15 text-white placeholder:text-gray-500"
                    />
                  </div>
                  <div>
                    <Label htmlFor="macro-name" className="text-xs text-gray-300">Neved <span className="text-gray-500">(opcionális)</span></Label>
                    <Input
                      id="macro-name"
                      data-testid="aesthetics-macro-name-input"
                      value={nameInput}
                      onChange={(e) => setNameInput(e.target.value)}
                      placeholder="pl. János"
                      className="mt-1 bg-white/5 border-white/15 text-white placeholder:text-gray-500"
                    />
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    type="submit"
                    disabled={isSending}
                    data-testid="aesthetics-macro-email-submit-btn"
                    className="bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-semibold"
                  >
                    {isSending ? 'Küldés…' : 'Küldés'}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => setShowEmailForm(false)}
                    className="text-gray-400 hover:text-white hover:bg-white/5"
                  >
                    Mégse
                  </Button>
                </div>
                <p className="text-[11px] text-gray-500 leading-relaxed">
                  Az e-mail címedet kizárólag a makró-terv kiküldésére és későbbi hasznos edzéstippekre használjuk.
                  Bármikor leiratkozhatsz az e-mail végén lévő linkkel.
                </p>
              </form>
            )}
          </div>

          {/* Recommended program — Félprivát Rekreációs Program.
              Copy adapts to the user's goal: fat-loss users read it as an
              "ideal safe start" pitch; maintenance/muscle-gain users read
              it as a "középhaladó" continuation pitch. Both flavours funnel
              to the same #programs anchor. */}
          {(() => {
            const goal = result.goal;
            const isBeginnerVibe = goal === 'fat_loss';
            const headline = isBeginnerVibe
              ? 'Ideális kezdéshez — kis csoportban, egyénre szabott terv'
              : 'Középhaladóknak ideális — folytasd struktúrában és közösségben';
            const line = isBeginnerVibe
              ? 'Kezdőknek és visszatérőknek: max. 3 fős zárt kiscsoport, ítélkezésmentes közeg, egyéni terv.'
              : 'Középhaladó fázisra tökéletes: állandó társasággal, tudományosan megalapozott felépítéssel, fix heti időpontban.';
            return (
              <div
                data-testid="macro-recommend-semi-private"
                className="rounded-xl border border-emerald-300 bg-gradient-to-br from-emerald-50 to-white p-5 md:p-6 overflow-hidden"
              >
                <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="inline-flex items-center gap-2 text-[10px] font-bold tracking-widest uppercase text-emerald-700 mb-2">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span>
                      Neked ajánljuk
                    </div>
                    <h4 className="text-lg md:text-xl font-bold text-gray-900 leading-tight break-words" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                      {headline}
                    </h4>
                    <p className="text-sm text-gray-700 mt-2 leading-relaxed break-words">{line}</p>
                    <p className="text-xs text-emerald-800 mt-2 font-semibold break-words">
                      Kiscsoportos Személyi Edzés — Duó & Trió · havi bérlet, fejenként a legkedvezőbb egységár (alkalomra vetítve 2.750 – 3.750 Ft / fő)
                    </p>
                  </div>
                  <a
                    href="#programs"
                    data-testid="macro-recommend-semi-private-cta"
                    onClick={handleCheckoutCtaClick}
                    className="inline-flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-500 text-white font-bold px-5 py-3 rounded-lg transition-all w-full sm:w-auto sm:whitespace-nowrap text-center"
                  >
                    Megnézem <ArrowRight size={16} />
                  </a>
                </div>
              </div>
            );
          })()}

          {/* Phase 2 → Checkout CTA */}
          <div
            data-testid="aesthetics-macro-cta"
            className="bg-gradient-to-r from-yellow-500 to-yellow-600 text-gray-900 rounded-xl p-5 md:p-7 flex flex-col md:flex-row md:items-center md:justify-between gap-4 overflow-hidden"
          >
            <div className="min-w-0 md:max-w-md">
              <div className="text-[10px] font-bold tracking-widest uppercase mb-1">Következő lépés</div>
              <h4 className="text-lg md:text-xl font-bold leading-tight break-words" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                Add hozzá a tervet a célokhoz – és építsünk együtt
              </h4>
              <p className="text-sm text-gray-900/70 mt-1 break-words">
                Válassz csomagot — a vásárlás után a személyre szabott makróid bekerülnek
                az üdvözlő e-mailedbe.
              </p>
            </div>
            <a
              href="#programs"
              data-testid="aesthetics-macro-checkout-btn"
              onClick={handleCheckoutCtaClick}
              className="inline-flex items-center justify-center gap-2 bg-gray-900 text-white font-bold px-6 py-3 rounded-lg hover:bg-gray-800 transition-all w-full md:w-auto md:whitespace-nowrap text-center"
            >
              Csomagok megtekintése <ArrowRight size={16} />
            </a>
          </div>
        </div>
      )}
    </section>
  );
};

export default MacroPhase;
